import { RateTableType } from "@redotech/redo-conversion-checkout-shipping-common/shipping-rate-table-enums";
import { ShopifyPlan } from "@redotech/redo-shopify-common/shopify-plan";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE } from "../checkouts/checkout-ab-test-utils";
import {
  CheckoutTreeNode,
  MAX_PRODUCT_TAGS_SEGMENT_NODES,
  SegmentableCheckoutCharacteristic,
} from "../checkouts/checkout-tree";
import { countSegmentNodesOfType } from "../checkouts/checkout-tree-traverse";
import { CheckoutExperience } from "../integration/redo-in-shipping/ris";
import { ShippingDiscount } from "../integration/redo-in-shipping/shipping-rate-table";
import {
  ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE,
  CheckoutAnalyticsVersion,
  getCheckoutAnalyticsVersionForSplit,
  isAdvancedCheckoutAnalyticsVersion,
} from "./checkout-conversion-metrics/analytics";

/**
 * Currently, whether advanced checkout features are available is determined by
 * whether the team has Shopify Plus. This check is thin right now, but it
 * exists to centralize the logic for checking if advanced checkout features
 * are available (to easily track all the features that are conditioned on it).
 */
export function isAdvancedCheckoutFeaturesAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return (
    hasShopifyPlus ||
    planDisplayName === "Plus Trial" ||
    !!(teamId && SHOP_PLUS_OVERRIDE_TEAM_IDS.includes(teamId))
  );
}

const SHOP_PLUS_OVERRIDE_TEAM_IDS = [
  // Dev teams
  "67e58c8b02978c77ac941989", // [Dev] Redo | Coleman 2
  "67d334c95d63bb9f8f7aa3ac", // [Dev] Redo | Ben Carper 2
];

/**
 * Whether a (non-Shop-Plus) team is eligible for the granular non-Plus checkout
 * analytics version (backed by the CHECKOUT_RATES_SHOWN + ORDER_CREATED mart).
 * Feeds the `shouldUseNonPlusGranular` input of
 * getCheckoutAnalyticsVersionForSplit.
 *
 * Currently enabled for ALL non-Shop-Plus teams. Shop+ teams keep the richer
 * advanced versions — they take precedence in the resolver, and because the
 * non-Plus cutover date is later than every advanced date, a Shop+ split can
 * never fall through to the non-Plus branch. The resolver additionally only
 * routes splits started after NON_PLUS_GRANULAR_ANALYTICS_CUTOVER_DATE to the
 * new mart, so older splits stay on TestMetric. Kept as the single rollout
 * switch: return false to disable, or reintroduce a team allow-list to scope it.
 */
export function isNonPlusGranularAnalyticsAvailable(): boolean {
  return true;
}

export function isCheckoutMetricsInsightsAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus,
    planDisplayName,
    teamId,
  });
}

export function shouldShowSegmentedCheckoutMetricsTable({
  hasShopifyPlus,
  planDisplayName,
  splitStartDate,
  splitAnalyticsVersionOverride,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
  teamId: string;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  const _isAdvancedCheckoutAnalyticsVersion =
    isAdvancedCheckoutAnalyticsVersion(analyticsVersion);
  const hasStarted = isDef(splitStartDate);
  const startDateIsAfterMetricsAvailableDate =
    splitStartDate &&
    splitStartDate.getTime() >
      ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE.getTime();
  return (
    _isAdvancedCheckoutAnalyticsVersion &&
    hasStarted &&
    !!startDateIsAfterMetricsAvailableDate
  );
}

export function shouldShowCustomVisualsForSplit({
  hasShopifyPlus,
  planDisplayName,
  teamId,
  splitStartDate,
  splitAnalyticsVersionOverride,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  switch (analyticsVersion) {
    case "advanced-v09-16-2025":
      // Custom visuals rely on the existence of the advanced checkout delivery
      // options metrics existing. This is the first version with those metrics
      // available.
      return true;
    case "advanced-v08-13-2025":
    case "basic":
    case "non-plus-v06-02-2026":
      // Non-Plus has no delivery-options metrics, so (like basic/v08-13) no
      // custom visuals.
      return false;
    default:
      return assertNever(analyticsVersion);
  }
}

export function isCheckoutWidgetsAvailable({
  shopifyPlan,
  planDisplayName,
  teamId,
}: {
  shopifyPlan: ShopifyPlan;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus: shopifyPlan === ShopifyPlan.PLUS,
    planDisplayName,
    teamId,
  });
}

/**
 * Checks if predictive conversion is enabled, regardless of whether a
 * SageMaker endpoint is live and configured with a model.
 */
export function isPredictiveConversionEnabled({
  predictiveConversion,
}: {
  predictiveConversion: { enabled?: boolean } | undefined;
}): boolean {
  return !!predictiveConversion?.enabled;
}

// ─────────────────────────────────────────────────────────────────────────────
//   Shipping option metafield feature compatibility
//
//   When `useShippingOptionMetafields` is enabled, the delivery customization
//   function can no longer parse encoded service codes. Several features that
//   depend on service code data (rate_table_id, treatment_ids, redo_coverage,
//   preview_mode) stop working. These functions detect incompatible features
//   and guard against enabling conflicting configurations.
//
//   Discount rules are NOT in this set: they run in a separate Shopify
//   discount function (rco-discount-rules) that reads its config from a
//   discount-app metafield and does not depend on service-code encoding, so
//   it is compatible with shipping option metafields.
//
//   IMPORTANT: The `activeCheckoutExperiences` param should be the set of experiences
//   reachable from the active checkout tree, not the full
//   shippingCoverage.activeCheckoutExperiences array.
// ─────────────────────────────────────────────────────────────────────────────

interface CheckoutOptimizationSettingsForMetafieldCheck {
  useShippingOptionMetafields?: boolean | null;
  isUsingDiscountRules?: boolean | null;
}

export function isUsingShippingOptionMetafields({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !!checkoutOptimizationSettings?.useShippingOptionMetafields;
}

export function isUsingAutoSelectedShippingOptions({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<
    CheckoutExperience,
    "customShippingMethodSortOrder"
  >[];
}): boolean {
  return activeCheckoutExperiences.some(
    (ce) =>
      isDef(ce.customShippingMethodSortOrder) &&
      ce.customShippingMethodSortOrder.length > 0,
  );
}

export function isUsingChris({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "rateTables">[];
}): boolean {
  return activeCheckoutExperiences.some((ce) =>
    ce.rateTables.some((rt) => {
      const hasCoverage =
        rt.coverageTypes?.return === true ||
        rt.coverageTypes?.packageProtection === true;
      const hasMerchantPaidCoverage = (() => {
        if (rt.type !== RateTableType.FIXED) {
          return false;
        }
        return rt.rates.some(
          (rate) =>
            rate.merchantPaidCoverageTypes?.return === true ||
            rate.merchantPaidCoverageTypes?.packageProtection === true,
        );
      })();
      return hasCoverage || hasMerchantPaidCoverage;
    }),
  );
}

export function isUsingHalfRedoAbTest({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "_id">[];
}): boolean {
  return activeCheckoutExperiences.some(
    (ce) =>
      ce._id === SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE._id,
  );
}

export function isUsingShippingOptionDiscounts({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "rateTables">[];
}): boolean {
  return activeCheckoutExperiences.some((ce) =>
    ce.rateTables.some(
      (rt) =>
        rt.shippingDiscountRules &&
        rt.shippingDiscountRules.some((rule) =>
          hasNonZeroShippingDiscount(rule.discount),
        ),
    ),
  );
}

function hasNonZeroShippingDiscount(discount: ShippingDiscount): boolean {
  if (isDef(discount.percentage)) {
    const num = parseFloat(discount.percentage);
    if (!isNaN(num) && num !== 0) {
      return true;
    }
  }
  if (isDef(discount.amount)) {
    const num = parseFloat(discount.amount.value);
    if (!isNaN(num) && num !== 0) {
      return true;
    }
  }
  return false;
}

/**
 * Returns true if shipping option metafields can be enabled. This requires
 * that no incompatible features are in use across the active checkout
 * experiences.
 */
export function canUseShippingOptionMetafields({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: CheckoutExperience[];
}): boolean {
  return (
    !isUsingAutoSelectedShippingOptions({ activeCheckoutExperiences }) &&
    !isUsingChris({ activeCheckoutExperiences }) &&
    !isUsingHalfRedoAbTest({ activeCheckoutExperiences }) &&
    !isUsingShippingOptionDiscounts({ activeCheckoutExperiences })
  );
}

export function canUseAutoSelectedShippingOptions({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function canUseChris({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function canUseHalfRedoAbTest({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function isUsingDiscountRules({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !!checkoutOptimizationSettings?.isUsingDiscountRules;
}

export function canUseDiscountRules({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "rateTables">[];
}): { canUse: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (isUsingShippingOptionDiscounts({ activeCheckoutExperiences })) {
    reasons.push(
      "Shipping discounts are configured on one or more shipping methods. Remove them before enabling discount rules",
    );
  }
  return { canUse: reasons.length === 0, reasons };
}

export function canUseShippingOptionDiscounts({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return (
    !isUsingShippingOptionMetafields({ checkoutOptimizationSettings }) &&
    !isUsingDiscountRules({ checkoutOptimizationSettings })
  );
}

export function canUsePreviewMode({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

// Product tags segment limits
// ============================

/**
 * Returns true if the checkout tree has more product tags segment nodes than the
 * standard limit (which is constrained by Shopify's delivery customization
 * function input variables). Trees in subscriptions mode are allowed to exceed
 * this limit because the delivery customization function does not evaluate
 * product tags segments in that mode.
 */
export function hasExcessProductTagsSegments({
  checkoutTreeRootNode,
}: {
  checkoutTreeRootNode: CheckoutTreeNode | null | undefined;
}): boolean {
  const count = countSegmentNodesOfType({
    checkoutTreeNode: checkoutTreeRootNode ?? null,
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  });
  return count > MAX_PRODUCT_TAGS_SEGMENT_NODES;
}

/**
 * Checks whether subscriptions mode can be disabled for a team. Subscriptions
 * mode cannot be disabled if the active checkout tree has more product tags
 * segment nodes than the standard limit, because those extra nodes would fail
 * validation without subscriptions mode.
 */
export function canDisableSubscriptionsMode({
  checkoutTreeRootNode,
}: {
  checkoutTreeRootNode: CheckoutTreeNode | null | undefined;
}): { canDisable: true } | { canDisable: false; reason: string } {
  if (hasExcessProductTagsSegments({ checkoutTreeRootNode })) {
    return {
      canDisable: false,
      reason:
        "Checkout tree has more than 2 product tags segment nodes. Remove product tags segments before disabling subscriptions mode.",
    };
  }
  return { canDisable: true };
}
